import * as amplitude from "@amplitude/analytics-browser"

const amplitudeApiKey = import.meta.env.VITE_AMPLITUDE_API_KEY

if (amplitudeApiKey) {
  amplitude.init(amplitudeApiKey, {
    autocapture: true,
    serverZone: "EU",
  })
  // Session replay (the rrweb recorder) loads beside the app instead of in
  // its initial bundle. Recording still happens: it starts once the plugin
  // arrives, a moment after startup, within the session init() began.
  import("@amplitude/plugin-session-replay-browser")
    .then(({ sessionReplayPlugin }) => amplitude.add(sessionReplayPlugin()))
    .catch((error: unknown) => {
      // eslint-disable-next-line no-console
      console.warn("Amplitude: session replay failed to load.", error)
    })
} else {
  // eslint-disable-next-line no-console
  console.warn(
    "Amplitude: API key is not configured. Analytics will not be tracked.",
  )
}

if (import.meta.env.VITE_NODE_ENV === "local") {
  amplitude.setOptOut(true)
}

export enum AnalyticsEvent {
  // Critical Events
  QUESTIONNAIRE_UPLOADED = "questionnaire_uploaded",
  AUTO_FILL_STARTED = "auto_fill_started",
  AUTO_FILL_COMPLETED = "auto_fill_completed",
  ANSWER_EDITED = "answer_edited",
  QUESTIONNAIRE_COMPLETED = "questionnaire_completed",
  QUESTIONNAIRE_DOWNLOADED = "questionnaire_downloaded",

  // High-Value Events
  KNOWLEDGE_BASE_FILE_UPLOADED = "knowledge_base_file_uploaded",
  CHAT_MESSAGE_SENT = "chat_message_sent",
  CHAT_SOURCE_CLICKED = "chat_source_clicked",
  EXCEL_VIEW_TOGGLED = "excel_view_toggled",
  SHAREPOINT_SYNC_TRIGGERED = "sharepoint_sync_triggered",

  // Engagement & Retention Events
  ONBOARDING_STARTED = "onboarding_started",
  ONBOARDING_TOUR_COMPLETED = "onboarding_tour_completed",
  FILE_PREVIEWED = "file_previewed",
  HELP_REQUESTED = "help_requested",
  UPGRADE_MODAL_SHOWN = "upgrade_modal_shown",
  MC_UPSELL_CONTACT_CLICKED = "mc_upsell_contact_clicked",
  FEEDBACK_SUBMITTED = "feedback_submitted",
  MC_HOME_RADAR_NOTIFY_CLICKED = "mc_home_radar_notify_clicked",
  HELP_ARTICLE_RATED = "help_article_rated",
  HELP_SEARCH_MISSED = "help_search_missed",
  // Settings: page switches per visit, and how far the setup got.
  SETTINGS_PAGE_VIEWED = "settings_page_viewed",
  // One per inquiry the supplier actually gets; the first per org marks the
  // end of the time-to-first-inquiry funnel.
  SUPPLIER_INQUIRY_SENT = "supplier_inquiry_sent",
  // SAP Ariba checklist for suppliers from the Ariba landing page: the funnel
  // from signup to the first extension answer in Ariba.
  ARIBA_ONBOARDING_VIEWED = "ariba_onboarding_viewed",
  ARIBA_ONBOARDING_STEP_COMPLETED = "ariba_onboarding_step_completed",
  ARIBA_EXTENSION_STORE_CLICKED = "ariba_extension_store_clicked",
  ARIBA_ONBOARDING_DISMISSED = "ariba_onboarding_dismissed",

  // Error & Drop-off Events
  UPLOAD_FAILED = "upload_failed",
  AUTO_FILL_FAILED = "auto_fill_failed",
}

/**
 * Common analytics event tracker
 * @param event AnalyticsEvent enum value
 * @param properties Event properties (optional)
 */
export function trackEvent(
  event: AnalyticsEvent,
  properties?: Record<string, unknown>,
) {
  try {
    amplitude.track(event, properties)
  } catch {
    // fail silently
  }
}

// Re-export track from amplitude for backwards compatibility
export { track } from "@amplitude/analytics-browser"
export { amplitude }
